// Service hook (TS-2.5.01, Resource Accessor layer) - GET /api/products, shared across features
// the way useReferenceList is: the Product view page (grouping/rollup), the project form's picker
// and the admin catalogue all need the same read, so it lives in src/hooks rather than being
// duplicated per feature or reached across a feature boundary (the house's no-cross-feature-import
// rule - see decisions.md, this batch). Mirrors useReferenceList's shape: fetch on mount, refetch on
// push-channel invalidation for EntityTypes.Product, non-fatal by itself (a caller decides whether
// a failed fetch here blocks its screen or just degrades a picker).
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/ApiError";
import { EntityTypes } from "../api/generated/contracts";
import type { ProductRecord } from "../api/generated/contracts";
import { productsApi } from "../api/products";
import { useInvalidation } from "../realtime/useInvalidation";

export interface UseProductsResult {
  readonly products: readonly ProductRecord[] | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refresh: () => void;
}

/** `includeRetired` defaults to false, the picker-safe default (D-268); pass true for a catalogue screen. */
export function useProducts(includeRetired = false): UseProductsResult {
  const [products, setProducts] = useState<readonly ProductRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    void productsApi
      .listProducts(includeRetired)
      .then((next) => {
        setProducts(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The product list could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [includeRetired]);

  useEffect(refresh, [refresh]);
  useInvalidation(refresh, EntityTypes.Product);

  return { products, loading, error, refresh };
}
